import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { trackAddToCart } from '../utils/fbPixel';
import { apiErrorMessage } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe usarse dentro de CartProvider');
  }
  return context;
};

// Generate or retrieve session ID for guest users
const getSessionId = () => {
  let sessionId = localStorage.getItem('alphatica_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('alphatica_session_id', sessionId);
  }
  return sessionId;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({
    subtotal: 0,
    discount_amount: 0,
    tax_amount: 0,
    shipping_cost: 0,
    total: 0,
    free_shipping: false
  });
  const [coupon, setCoupon] = useState(null);
  // Estado del regalo de la promo (5x3): lo calcula el servidor, aquí solo se muestra
  const [gifts, setGifts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cartId, setCartId] = useState(null);

  const sessionId = getSessionId();

  // Fetch cart from API
  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/cart?session_id=${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setItems(data.cart?.items || []);
        setTotals(data.totals || {});
        setCoupon(data.coupon || null);
        setGifts(data.gifts || null);
        setCartId(data.cart?.id || null);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // Load cart on mount
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Add item to cart
  const addItem = async (product, sizeMl = 100) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/cart/items?session_id=${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: product.id,
          quantity: 1,
          size_ml: sizeMl
        })
      });

      if (response.ok) {
        await fetchCart();
        // Track AddToCart for Facebook Pixel
        const priceForSize = sizeMl === 40 ? (product.price_40ml || 200) : product.price;
        trackAddToCart({ ...product, price: priceForSize, size_ml: sizeMl }, 1);
      } else {
        const error = await response.json();
        toast.error(apiErrorMessage(error, 'Error al añadir al carrito'));
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // Remove item from cart
  const removeItem = async (productId, sizeMl = 100) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/cart/items/${productId}?session_id=${sessionId}&size_ml=${sizeMl}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        await fetchCart();
        toast.success('Producto eliminado del carrito');
      } else {
        const error = await response.json();
        toast.error(apiErrorMessage(error, 'Error al eliminar del carrito'));
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // Update item quantity
  const updateQuantity = async (productId, quantity, sizeMl = 100) => {
    if (quantity <= 0) {
      return removeItem(productId, sizeMl);
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/cart/items/${productId}?session_id=${sessionId}&quantity=${quantity}&size_ml=${sizeMl}`,
        { method: 'PUT' }
      );

      if (response.ok) {
        await fetchCart();
      } else {
        const error = await response.json();
        toast.error(apiErrorMessage(error, 'Error al actualizar cantidad'));
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // Apply coupon
  const applyCoupon = async (code) => {
    try {
      setLoading(true);
      const customerEmail = localStorage.getItem('alphatica_customer_email');
      const emailParam = customerEmail ? `&customer_email=${encodeURIComponent(customerEmail)}` : '';
      const response = await fetch(
        `${API_URL}/api/cart/coupon?session_id=${sessionId}&code=${code}${emailParam}`,
        { method: 'POST' }
      );

      if (response.ok) {
        await fetchCart();
        toast.success('Cupón aplicado correctamente');
        return true;
      } else {
        const error = await response.json();
        toast.error(apiErrorMessage(error, 'Cupón inválido'));
        return false;
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      toast.error('Error de conexión');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Remove coupon
  const removeCoupon = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/cart/coupon?session_id=${sessionId}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        await fetchCart();
        toast.success('Cupón removido');
      }
    } catch (error) {
      console.error('Error removing coupon:', error);
    } finally {
      setLoading(false);
    }
  };

  // Clear cart
  const clearCart = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/cart?session_id=${sessionId}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        setItems([]);
        setTotals({
          subtotal: 0,
          discount_amount: 0,
          tax_amount: 0,
          shipping_cost: 0,
          total: 0,
          free_shipping: false
        });
        setCoupon(null);
        setGifts(null);
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // Elegir el/los perfume(s) de 10ml de regalo. El servidor valida todo (cupo, stock, promo vigente).
  const setGiftPicks = async (productIds) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/cart/gifts?session_id=${sessionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: productIds })
      });

      if (response.ok) {
        await fetchCart();
        return true;
      }
      const error = await response.json().catch(() => ({}));
      toast.error(apiErrorMessage(error, 'No se pudo guardar tu regalo'));
      await fetchCart();
      return false;
    } catch (error) {
      console.error('Error setting gift picks:', error);
      toast.error('Error de conexión');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get total items count
  const getItemCount = () => {
    return items.reduce((count, item) => count + item.quantity, 0);
  };

  // Get subtotal
  const getTotal = () => {
    return totals.total || 0;
  };

  const value = {
    items,
    totals,
    coupon,
    gifts,
    loading,
    cartId,
    sessionId,
    addItem,
    removeItem,
    updateQuantity,
    applyCoupon,
    removeCoupon,
    clearCart,
    setGiftPicks,
    getTotal,
    getItemCount,
    refreshCart: fetchCart
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
